import { ProductImageRendition } from "@omni/types/api/product/shared/enums/productImageRendition.enum";
import classnames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { SlideThumbnail } from "@/components/Product/ImageViewer/partials/SlideThumbnail/slideThumbnail.component";
import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { Image } from "@/components/Product/ProductImage/imageWrapper/image/image.component";
import { EmptyImages } from "@/utils/enums";
import { stopVideo } from "@/utils/imageViewer";

import "./imageViewer.scss";

import { ImageViewerImage } from "@omni/types/api/product/bff/shared/imageViewerImage.model";
import { ImageViewerVideo } from "@omni/types/api/product/bff/shared/imageViewerVideo.model";
import { cn } from "@omni/utils/styles";

import { ImageViewerModal } from "@/components/Modals/ImageViewerModal/imageViewerModal";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { DamImage } from "@/utils/dam.utils";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

type Props = {
    title?: string;
    images: Array<ImageViewerImage>;
    videos: Array<ImageViewerVideo>;
    activeIndex: any;
    setActiveIndex: any;
    usesDefaultImage: boolean;
    defaultVideoThumbnailPath?: string;
    ecoChequesAccepted: boolean;
};

const ImageViewer = ({
    title,
    images,
    videos,
    usesDefaultImage,
    setActiveIndex,
    activeIndex,
    defaultVideoThumbnailPath,
    ecoChequesAccepted
}: Props) => {
    const { t } = useGranite();

    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [thumbSwiper, setThumbSwiper] = useState<SwiperCore>();

    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const { open: openModal } = useModalRouter(ModalType.ImageViewer);

    const mediaList = useMemo(() => [...images, ...videos], [images, videos]);

    const amount = mediaList.length;

    useEffect(() => {
        const hashChangeHandler = () => {
            stopVideo();
        };

        window.addEventListener("hashchange", hashChangeHandler);
        return () =>
            window.removeEventListener("hashchange", hashChangeHandler);
    }, []);

    useEffect(() => {
        if (mainSwiper && thumbSwiper) {
            if (thumbSwiper) {
                thumbSwiper.update();
            }

            if (mainSwiper?.activeIndex !== activeIndex) {
                mainSwiper.slideTo(activeIndex);
            }
        }
    }, [activeIndex, mainSwiper, thumbSwiper]);

    const showSlider =
        (images && images.length > 1) ||
        (images.length >= 1 && videos.length > 0);

    const handleOnZoom = () => {
        openModal();
        mainSwiper && setActiveIndex(mainSwiper.activeIndex);
    };

    return (
        <div
            className="imageViewer pr-3"
            data-test="imageViewer"
        >
            <div
                data-test="imageViewer-gallery-slider"
                className="imageViewer__gallery-slider"
            >
                <Swiper
                    modules={[Thumbs]}
                    thumbs={{ swiper: thumbSwiper }}
                    spaceBetween={10}
                    slidesPerView={1}
                    centeredSlides
                    onActiveIndexChange={(swiper) => {
                        setActiveIndex(swiper.activeIndex);
                        stopVideo();
                    }}
                    onSwiper={setMainSwiper}
                >
                    {images
                        ?.filter((img) => !!img.main)
                        .map((image, index) => (
                            <SwiperSlide
                                key={`main${index}`}
                                tag={"button"}
                                data-test={`imageViewer-gallery-slider-image-${image.altText}`}
                                onClick={handleOnZoom}
                            >
                                <Image
                                    lazyLoad={index > 0}
                                    parentFit={false}
                                    altText={image.altText}
                                    defaultImageUrl={EmptyImages.Size_120_90}
                                    medias={[
                                        {
                                            altText: image.altText,
                                            url: image.main!,
                                            format: ProductImageRendition.XS
                                        }
                                    ]}
                                    rendition={ProductImageRendition.XS}
                                />
                                {ecoChequesAccepted && (
                                    <img
                                        className="imageViewer__eco-label"
                                        src={DamImage.icon.label.eco}
                                        alt="ecocheques"
                                    />
                                )}
                            </SwiperSlide>
                        ))}
                    {videos?.map((video, index) => (
                        <SwiperSlide
                            key={`main-video-${index}`}
                            data-test="imageViewer-gallery-slider-video"
                            className="imageViewer__gallery-slide
                                            imageViewer-mediaSlide
                                            embed-responsive
                                            embed-responsive-16by9"
                        >
                            <iframe
                                className="d-block w-100 h-100 product-video"
                                id={`imageViewerIframe-${index}`}
                                src={`${video.main}`}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            {!usesDefaultImage && (
                <div
                    className={cn("imageViewer__zoom-link-holder", {
                        "pt-5 pt-md-0": ecoChequesAccepted
                    })}
                    onClick={handleOnZoom}
                >
                    <p className="btn-link">
                        <i className="las la-search-plus la-lg" />
                        <span className="ml-1">
                            {t("productDetail.imageViewer.link.zoom")}
                        </span>
                    </p>
                </div>
            )}
            {showSlider && (
                <div>
                    <div
                        data-test="imageViewer-thumbnail-slider"
                        className="imageViewer__thumbnail-slider d-none d-md-flex align-items-center slider-layout-auto"
                    >
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationPrevRef}
                                disabled={activeIndex === 0}
                                icon="left"
                                onClick={() => {
                                    if (activeIndex !== 0) {
                                        mainSwiper?.slidePrev();
                                        thumbSwiper?.slidePrev();
                                    }
                                }}
                            />
                        )}
                        <Swiper
                            onSwiper={setThumbSwiper}
                            spaceBetween={10}
                            slidesPerView={"auto"}
                            threshold={2}
                            navigation={{
                                nextEl: navigationNextRef.current,
                                prevEl: navigationPrevRef.current
                            }}
                            modules={[Thumbs, Navigation]}
                            watchSlidesProgress
                        >
                            {mediaList.map((media, index) => (
                                <SwiperSlide
                                    tag="button"
                                    key={`thumb${index}`}
                                    className={classnames(
                                        "ssr-slide my-1 imageViewer-thumbnail bg-transparent",
                                        index === activeIndex &&
                                            "imageViewer-activeThumb"
                                    )}
                                    onClick={() => {
                                        setActiveIndex(index);
                                    }}
                                >
                                    <SlideThumbnail
                                        lazyLoad={index > 0}
                                        media={media}
                                        defaultVideoThumbnailPath={
                                            defaultVideoThumbnailPath
                                        }
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationNextRef}
                                disabled={activeIndex + 1 === amount}
                                icon="right"
                                onClick={() => {
                                    if (activeIndex + 1 !== amount) {
                                        mainSwiper?.slideNext();
                                        thumbSwiper?.slideNext();
                                    }
                                }}
                            />
                        )}
                    </div>
                    <div
                        data-test="imageViewer-amountHolder"
                        className="imageViewer-amountHolder d-flex d-md-none align-items-center justify-content-center"
                    >
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationPrevRef}
                                disabled={activeIndex === 0}
                                icon="left"
                                onClick={() => {
                                    if (activeIndex !== 0 && mainSwiper) {
                                        mainSwiper.slidePrev();
                                        setActiveIndex(mainSwiper.activeIndex);
                                    }
                                }}
                            />
                        )}
                        <p
                            data-test="imageViewer-amountHolder-amount"
                            className="m-0"
                        >
                            {`${activeIndex + 1} van ${amount}`}
                        </p>
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationNextRef}
                                disabled={activeIndex + 1 === amount}
                                icon="right"
                                onClick={() => {
                                    if (
                                        activeIndex + 1 !== amount &&
                                        mainSwiper
                                    ) {
                                        mainSwiper.slideNext();
                                        setActiveIndex(mainSwiper.activeIndex);
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
            <ImageViewerModal
                title={title}
                images={images}
                videos={videos}
                defaultVideoThumbnailPath={defaultVideoThumbnailPath}
            />
        </div>
    );
};

export { ImageViewer };
